import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import type { Category, Product, ProductInput, ProductStatus } from "@/types/models";
import { brlPriceRegex, centsToBrlInput, parseBrlToCents } from "@/lib/price";
import { optionalSlugSchema, slugInputValue } from "@/lib/slug";

const optionalBrlPrice = z
  .string()
  .refine((val) => val === "" || brlPriceRegex.test(val.trim()), {
    message: "Preço inválido. Use formato 199,90",
  });

const productSchema = z.object({
  name: z.string().min(1, "Nome é obrigatório"),
  slug: optionalSlugSchema,
  description: z.string(),
  price: z
    .string()
    .min(1, "Preço é obrigatório")
    .refine((val) => brlPriceRegex.test(val.trim()), {
      message: "Preço inválido. Use formato 199,90",
    }),
  compareAtPrice: optionalBrlPrice,
  sku: z.string(),
  categoryId: z.string(),
  status: z.enum(["draft", "active", "archived"]),
});

export type ProductFormValues = z.infer<typeof productSchema>;

const defaultValues: ProductFormValues = {
  name: "",
  slug: "",
  description: "",
  price: "",
  compareAtPrice: "",
  sku: "",
  categoryId: "",
  status: "draft",
};

const statusToGraphql: Record<ProductFormValues["status"], ProductStatus> = {
  draft: "DRAFT",
  active: "ACTIVE",
  archived: "ARCHIVED",
};

const statusFromGraphql: Record<ProductStatus, ProductFormValues["status"]> = {
  DRAFT: "draft",
  ACTIVE: "active",
  ARCHIVED: "archived",
};

function toProductInput(values: ProductFormValues, id?: string): ProductInput {
  const compareCents =
    values.compareAtPrice.trim() === "" ? null : parseBrlToCents(values.compareAtPrice);

  const input: ProductInput = {
    name: values.name.trim(),
    description: values.description.trim() || null,
    priceCents: parseBrlToCents(values.price),
    compareAtPriceCents: compareCents,
    sku: values.sku.trim() || null,
    categoryId: values.categoryId || null,
    status: statusToGraphql[values.status],
  };

  if (id) input.id = id;

  const slug = slugInputValue(values.slug);
  if (slug) input.slug = slug;

  return input;
}

export interface ProductFormProps {
  product?: Product | null;
  categories?: Category[];
  saving?: boolean;
  onSubmit: (input: ProductInput) => void;
  onCancel?: () => void;
}

export function ProductForm({
  product,
  categories = [],
  saving = false,
  onSubmit,
  onCancel,
}: ProductFormProps) {
  const [actionError, setActionError] = useState<string | null>(null);
  const isEditMode = Boolean(product);

  const form = useForm<ProductFormValues>({
    resolver: zodResolver(productSchema),
    defaultValues,
  });

  useEffect(() => {
    if (product) {
      form.reset({
        name: product.name,
        slug: product.slug,
        description: product.description ?? "",
        price: centsToBrlInput(product.priceCents),
        compareAtPrice:
          product.compareAtPriceCents != null ? centsToBrlInput(product.compareAtPriceCents) : "",
        sku: product.sku ?? "",
        categoryId: product.categoryId ?? "",
        status: statusFromGraphql[product.status],
      });
      return;
    }

    form.reset(defaultValues);
  }, [product, form]);

  const handleSubmit = (values: ProductFormValues) => {
    setActionError(null);
    onSubmit(toProductInput(values, product?.id));
  };

  return (
    <section className="border border-primary-200 dark:border-primary-800 p-8">
      <h2 className="text-xl font-light text-primary-900 dark:text-white mb-6">
        {isEditMode ? "Editar produto" : "Novo produto"}
      </h2>

      {actionError && (
        <Alert variant="destructive" className="mb-6">
          <AlertTitle>Erro</AlertTitle>
          <AlertDescription>{actionError}</AlertDescription>
        </Alert>
      )}

      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-8 max-w-2xl">
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Nome</FormLabel>
                <FormControl>
                  <Input {...field} placeholder="Ex: Camiseta básica" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="slug"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Slug (opcional)</FormLabel>
                <FormControl>
                  <Input {...field} placeholder="Gerado automaticamente a partir do nome" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="description"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Descrição</FormLabel>
                <FormControl>
                  <textarea
                    {...field}
                    rows={4}
                    placeholder="Descrição do produto"
                    className="flex w-full border border-primary-200 dark:border-primary-700 bg-transparent px-3 py-2 text-sm font-light text-primary-900 dark:text-white placeholder:text-primary-400 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary-900 dark:focus-visible:ring-white"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="grid gap-6 sm:grid-cols-2">
            <FormField
              control={form.control}
              name="price"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Preço (R$)</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="199,90" inputMode="decimal" />
                  </FormControl>
                  <p className="text-xs font-light text-primary-500 dark:text-primary-500">
                    Use a ação Precificar na lista de produtos para calcular com receita e custos.
                  </p>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="compareAtPrice"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Preço comparativo (opcional)</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="249,90" inputMode="decimal" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <FormField
            control={form.control}
            name="sku"
            render={({ field }) => (
              <FormItem>
                <FormLabel>SKU (opcional)</FormLabel>
                <FormControl>
                  <Input {...field} placeholder="CAM-001" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="categoryId"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Categoria</FormLabel>
                <FormControl>
                  <select
                    {...field}
                    className="flex h-10 w-full border border-primary-200 dark:border-primary-700 bg-transparent px-3 py-2 text-sm font-light text-primary-900 dark:text-white focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary-900 dark:focus-visible:ring-white"
                  >
                    <option value="">Sem categoria</option>
                    {categories.map((category) => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="status"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Status</FormLabel>
                <FormControl>
                  <select
                    {...field}
                    className="flex h-10 w-full border border-primary-200 dark:border-primary-700 bg-transparent px-3 py-2 text-sm font-light text-primary-900 dark:text-white focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary-900 dark:focus-visible:ring-white"
                  >
                    <option value="draft">Rascunho</option>
                    <option value="active">Ativo</option>
                    <option value="archived">Arquivado</option>
                  </select>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="flex flex-wrap gap-3">
            <Button type="submit" disabled={saving}>
              {saving ? "Salvando…" : isEditMode ? "Salvar alterações" : "Criar produto"}
            </Button>
            {onCancel && (
              <Button type="button" variant="outline" onClick={onCancel}>
                Cancelar
              </Button>
            )}
          </div>
        </form>
      </Form>
    </section>
  );
}
